import React, { useEffect, useMemo, useRef, useState } from "react";
import { DockviewApi } from "dockview-react";
import { useNavigate } from "react-router-dom";
import { api } from "./utils/api";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { RealtimeLwChart, RtCandle, RtChartData } from "./RealtimeLwChart";

// Compact real-time chart embedded under a ClearCross tweet: Invcom / Yahoo tabs, range + resolution radios and
// IB Fast (same endpoints and chart as the Real-time Chart window). The chart is only fetched and shown once the
// user opens it (show, toggled from the tweet header); bars then refresh while it is on screen. IB Fast is always shown.

type Tab = "invcom" | "yahoo";

// combined resolution / range presets: label -> invcom resolution, Yahoo interval, days back
const PRESETS = [
  { label: "min/d", invcom: "1", yahoo: "1m", days: 1 },
  { label: "min/w", invcom: "1", yahoo: "1m", days: 7 },
  { label: "h/m", invcom: "60", yahoo: "60m", days: 30 },
  { label: "h/6m", invcom: "60", yahoo: "60m", days: 180 },
  { label: "d/y", invcom: "D", yahoo: "1d", days: 365 },
  { label: "d/2y", invcom: "D", yahoo: "1d", days: 730 },
  { label: "d/5y", invcom: "D", yahoo: "1d", days: 1825 },
];
const DEFAULT_PRESET = 1; // min/w

const RISING = "#17BECF";
// terminal.css (global) makes labels / inputs full width on narrow screens; keep them inline
const AUTO_W: React.CSSProperties = { width: "auto" };
const NYC_TZ = "America/New_York";
const DETAILS_MS = 1000;

function formatNyc(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: NYC_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour").replace("24", "00")}:${get("minute")}`;
}

function extractCandles(data: any): RtCandle[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.chart_data)) return data.chart_data;
  if (Array.isArray(data?.candles)) return data.candles;
  return [];
}

// on screen = worth refreshing; off screen charts stay idle
function useOnScreen(ref: React.RefObject<HTMLElement | null>) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

export interface ClearCrossChartTicker {
  ticker: string;
  investing_id: number | null;
  dir?: number | null; // direction of the largest price-sigma move (+1 up / -1 down)
}

// IB Fast fades the spike: puts after an up move, calls after a down move (no direction: ticker only)
function ibFastMessage(t: ClearCrossChartTicker): string {
  const ticker = t.ticker.toUpperCase();
  if (t.dir && t.dir > 0) return `${ticker} p`;
  if (t.dir && t.dir < 0) return `${ticker} c`;
  return ticker;
}

export const ClearCrossChart = ({
  tickers,
  containerApi,
  show,
}: {
  tickers: ClearCrossChartTicker[];
  containerApi?: DockviewApi;
  show: boolean;
}) => {
  const box = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // fetch only while the chart is opened and on screen
  const visible = useOnScreen(box) && show;
  const [tab, setTab] = useState<Tab>("invcom");
  const [tickerIdx, setTickerIdx] = useState(0);
  const [preset, setPreset] = useState(PRESETS[DEFAULT_PRESET]);
  const [candles, setCandles] = useState<RtCandle[] | null>(null);
  const [lastPrice, setLastPrice] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [refreshCount, setRefreshCount] = useState(0);
  const loadedKey = useRef("");

  const t = tickers[Math.min(tickerIdx, tickers.length - 1)];
  const key = `${tab}|${t.ticker}|${preset.label}`;
  const resolution = tab === "invcom" ? preset.invcom : preset.yahoo;

  // a closed chart drops its bars: reopening fetches fresh ones
  useEffect(() => {
    if (show) return;
    setCandles(null);
    setError("");
    loadedKey.current = "";
  }, [show]);

  // bars: on load / setting change, then every 5 s (intraday) or 60 s (daily) while on screen
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const load = () => {
      const now = new Date();
      const from = formatNyc(new Date(now.getTime() - preset.days * 86_400_000)).split(" ")[0] + " 00:00";
      const to = formatNyc(new Date(now.getTime() + 60_000));
      const q = `resolution=${resolution}&from_date=${encodeURIComponent(from)}&to_date=${encodeURIComponent(to)}`;
      const url =
        tab === "yahoo"
          ? `/yahoo-chart?ticker=${encodeURIComponent(t.ticker)}&interval=${resolution}&from_date=${encodeURIComponent(from)}&to_date=${encodeURIComponent(to)}`
          : t.investing_id
            ? `/invcom-manual-chart?pair_ID=${t.investing_id}&${q}`
            : `/invcom-auto-chart?ticker=${encodeURIComponent(t.ticker)}&${q}`;
      api
        .get(url)
        .then((r) => {
          if (cancelled) return;
          setError("");
          setCandles(extractCandles(r.data));
          // a reload of the same settings keeps the zoom / scroll; new settings fit the chart
          if (loadedKey.current === key) setRefreshCount((n) => n + 1);
          loadedKey.current = key;
        })
        .catch((e) => !cancelled && setError(e.response?.data?.detail || e.response?.data?.message || "Error loading chart"));
    };
    if (loadedKey.current !== key) setCandles(null);
    load();
    const id = setInterval(load, preset.invcom === "D" ? 60_000 : 5_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, key]);

  // live price line, every second while on screen
  useEffect(() => {
    setLastPrice(null);
    if (!visible) return;
    const url =
      tab === "yahoo"
        ? `/yahoo-details?ticker=${encodeURIComponent(t.ticker)}`
        : t.investing_id
          ? `/invcom-details?pair_ID=${t.investing_id}`
          : null;
    if (!url) return;
    const poll = () =>
      api
        .get(url)
        .then((r) => setLastPrice(r.data?.extended_price ?? r.data?.last ?? null))
        .catch(() => {});
    poll();
    const id = setInterval(poll, DETAILS_MS);
    return () => clearInterval(id);
  }, [visible, tab, t.ticker, t.investing_id]);

  // memoized: the parent list re-renders every second, the chart should only see new bars
  const data: RtChartData | null = useMemo(
    () => (candles && candles.length ? { tracks: [{ name: t.ticker, candles, color: RISING }] } : null),
    [candles, t.ticker],
  );
  // radio group names must be unique per chart (many charts in one list)
  const uid = useRef(Math.random().toString(36).slice(2)).current;
  const name = (group: string) => `cc-${group}-${uid}`;

  return (
    <div ref={box} className="sm:ml-6 mt-1 mb-2 text-sm">
      {/* row 1: source (+ ticker when the tweet has several) */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
        {show && (["invcom", "yahoo"] as Tab[]).map((v) => (
          <label key={v} className="flex items-center gap-1 cursor-pointer select-none" style={AUTO_W}>
            <input type="radio" style={AUTO_W} name={name("tab")} checked={tab === v} onChange={() => setTab(v)} />
            {v === "invcom" ? "Invcom" : "Yahoo"}
          </label>
        ))}
        {tickers.length > 1 && (
          <span className={`flex items-center gap-3 ${show ? "ml-4" : ""}`}>
            {tickers.map((x, i) => (
              <label key={x.ticker} className="flex items-center gap-1 cursor-pointer select-none font-semibold" style={AUTO_W}>
                <input type="radio" style={AUTO_W} name={name("tk")} checked={tickerIdx === i} onChange={() => setTickerIdx(i)} />
                {x.ticker}
              </label>
            ))}
          </span>
        )}
      </div>

      {/* row 2: resolution / range presets, IB Fast */}
      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-1">
        {show && PRESETS.map((p) => (
          <label key={p.label} className="flex items-center gap-1 cursor-pointer select-none" style={AUTO_W}>
            <input type="radio" style={AUTO_W} name={name("preset")} checked={preset.label === p.label} onChange={() => setPreset(p)} />
            {p.label}
          </label>
        ))}
        <button
          type="button"
          className={`btn btn-default text-xs ${show ? "ml-2" : ""}`}
          onClick={() => {
            const message = ibFastMessage(t);
            // desktop: floating IBKR chat window; mobile (no dockview): the mobile chat page
            if (containerApi) addFloatingPanel(containerApi, "ibkr_chat", { message });
            else navigate(`/mobile/ibkr-chat?message=${encodeURIComponent(message)}`);
          }}
          title={`Open IBKR Chat with "${ibFastMessage(t)}" (puts after an up spike, calls after a down spike)`}
        >
          IB Fast
        </button>
      </div>

      {/* row 3: chart (opened only) */}
      {show && error && <div className="text-red-600 mt-1">{error}</div>}
      {show && !visible && !data && <div style={{ height: 450 }} />}
      {visible && !data && !error && <div className="text-gray-500 mt-1" style={{ height: 450 }}>Loading…</div>}
      {show && data && (
        <RealtimeLwChart
          key={key}
          data={data}
          resolution={resolution}
          compress={false}
          lastPrice={lastPrice}
          refreshCount={refreshCount}
        />
      )}
    </div>
  );
};
